import * as Menu from '@radix-ui/react-dropdown-menu'
import { type ItemOf } from '@shared/board'
import { type Effort, type SessionMeta, type Trigger } from '@shared/session'
import { clsx } from 'clsx'
import {
  Check,
  ChevronDown,
  FileText,
  History,
  LayoutDashboard,
  MessagesSquare,
  PanelRightClose,
  Pencil,
  Plus,
  Send,
  Settings2,
  Square,
} from 'lucide-react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router'
import { IconButton } from '@/components/Button'
import { EditableTitle } from '@/components/EditableTitle'
import { MenuContent, menuItemClass } from '@/components/Menu'
import { useBoardCtx } from '@/board/context'
import { newSessionNode } from '@/board/factory'
import { useI18n } from '@/i18n/i18n'
import { actionStatus, viewActions } from './actions'
import { ActionsLine } from './ActionsLine'
import { ChatComposer } from './ChatComposer'
import { Avatar } from '@/components/Avatar'
import { formatUsd, sessionName } from './describe'
import { HandToAiMenu } from './HandToAiMenu'
import { MentionChip, SayText, TextWithPointers } from './SayText'
import { type PanelTab, TracePanel } from './TracePanel'
import { ChangeView, RevertNote } from './ChangeView'
import { type OpenTurnPanel, OpenTurnPanelContext, TurnIdContext } from './turnPanel'
import { BoardLine } from './BoardLine'
import { useSpeaker } from './actorName'
import { useSmoothText } from './smooth'
import { buildTurns, type Turn } from './turns'
import { type ChatVariant, ChatVariantContext, useChatVariant, useColumn } from './chatLayout'

const MODELS = [
  { id: 'claude-opus-5-5', label: 'Opus 5.5' },
  { id: 'claude-sonnet-5-5', label: 'Sonnet 5.5' },
  { id: 'claude-haiku-5-5', label: 'Haiku 5.5' },
  { id: 'claude-fable-5-1', label: 'Fable 5.1' },
]
const EFFORTS: Effort[] = ['low', 'medium', 'high', 'max']

// ---------------------------------------------------------------- header

function SessionHeader({
  trailing,
  onTrace,
  inline = false,
}: {
  trailing: ReactNode
  onTrace: () => void
  /** Inside the app header (chat-main on wide screens) rather than a bar of its own. */
  inline?: boolean
}) {
  const { sessions, client } = useBoardCtx()
  const { t, relativeTime } = useI18n()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const cur = snap.sessions.find((s) => s.id === snap.current)
  const readOnly = client.readOnly
  const [renaming, setRenaming] = useState(false)
  const renameRequested = useRef(false)
  const ordered = useMemo(
    () => [...snap.sessions].sort((a, b) => b.updatedAt - a.updatedAt),
    [snap.sessions],
  )

  return (
    <header
      className={clsx(
        'flex items-center gap-1',
        inline ? 'min-w-0' : 'h-12 flex-none border-b border-line px-2',
      )}
    >
      <Menu.Root>
        <Menu.Trigger
          className="flex min-w-0 items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-surface-2 data-[state=open]:bg-surface-2"
          aria-label={t('session.switch')}
        >
          {cur ? (
            <Avatar actor={{ kind: 'session', id: cur.id }} size={18} />
          ) : (
            <span className="size-[18px] flex-none rounded-full bg-line" />
          )}
          {!renaming && (
            <span className="truncate text-[13.5px] font-medium">
              {cur ? sessionName(cur, t('session.untitled')) : t('session.none')}
            </span>
          )}
          <ChevronDown size={14} className="flex-none text-muted" />
        </Menu.Trigger>
        <MenuContent
          onCloseAutoFocus={(e) => {
            if (renameRequested.current) e.preventDefault()
            renameRequested.current = false
          }}
        >
          <Menu.Label className="px-2 pt-1 pb-1 text-[11px] font-medium text-muted">
            {t('session.list')}
          </Menu.Label>
          {ordered.map((s) => (
            <Menu.Item
              key={s.id}
              className={menuItemClass}
              onSelect={() => {
                sessions.select(s.id)
              }}
            >
              <Avatar actor={{ kind: 'session', id: s.id }} />
              <span className="min-w-0 flex-1 truncate">
                {sessionName(s, t('session.untitled'))}
              </span>
              {s.running ? (
                <span
                  className="size-1.5 animate-pulse rounded-full bg-ai"
                  aria-label={t('session.running')}
                />
              ) : (
                <span className="text-[11px] text-muted">{relativeTime(s.updatedAt)}</span>
              )}
              {s.id === snap.current && <Check size={13} className="text-muted" />}
            </Menu.Item>
          ))}
          {ordered.length > 0 && !readOnly && <Menu.Separator className="my-1 h-px bg-line" />}
          {cur && !readOnly && <PlaceOnBoardItem sessionId={cur.id} />}
          {cur && !readOnly && (
            <Menu.Item
              className={menuItemClass}
              onSelect={() => {
                renameRequested.current = true
                setRenaming(true)
              }}
            >
              <Pencil size={13} className="text-muted" />
              {t('session.rename')}
            </Menu.Item>
          )}
          {!readOnly && (
            <Menu.Item
              className={menuItemClass}
              onSelect={() => {
                sessions.create()
              }}
            >
              <Plus size={14} className="text-muted" />
              {t('session.new')}
            </Menu.Item>
          )}
        </MenuContent>
      </Menu.Root>
      {cur && renaming && (
        <div className="min-w-0 flex-1">
          <EditableTitle
            value={cur.title}
            editing
            onEditingChange={setRenaming}
            maxLength={120}
            autosize
            placeholder={t('session.untitled')}
            label={t('session.titleLabel')}
            inputClassName="text-[13.5px] font-medium"
            onCommit={(title) => {
              sessions.update(cur.id, { title })
            }}
          />
        </div>
      )}
      {!inline && <span className="flex-1" />}
      {cur && !readOnly && !cur.external && <ModelPicker session={cur} />}
      <IconButton label={t('trace.open')} onClick={onTrace} disabled={!cur}>
        <History size={16} />
      </IconButton>
      {!readOnly && (
        <IconButton
          label={t('session.new')}
          onClick={() => {
            sessions.create()
          }}
        >
          <Plus size={16} />
        </IconButton>
      )}
      {trailing}
    </header>
  )
}

function ModelPicker({ session }: { session: SessionMeta }) {
  const { sessions } = useBoardCtx()
  const { t } = useI18n()
  const models = MODELS.some((m) => m.id === session.model)
    ? MODELS
    : [...MODELS, { id: session.model, label: session.model }]
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <IconButton label={t('session.model')}>
          <Settings2 size={16} />
        </IconButton>
      </Menu.Trigger>
      <MenuContent>
        <Menu.Label className="px-2 pt-1 pb-0.5 text-[11px] font-medium text-muted">
          {t('session.model')}
        </Menu.Label>
        <Menu.RadioGroup
          value={session.model}
          onValueChange={(model) => {
            sessions.update(session.id, { model })
          }}
        >
          {models.map((m) => (
            <Menu.RadioItem key={m.id} value={m.id} className={menuItemClass}>
              <span className="w-4">
                <Menu.ItemIndicator>
                  <Check size={14} />
                </Menu.ItemIndicator>
              </span>
              {m.label}
            </Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
        <Menu.Separator className="my-1 h-px bg-line" />
        <Menu.Label className="px-2 pt-1 pb-0.5 text-[11px] font-medium text-muted">
          {t('session.effort')}
        </Menu.Label>
        <Menu.RadioGroup
          value={session.effort}
          onValueChange={(effort) => {
            sessions.update(session.id, { effort: effort as Effort })
          }}
        >
          {EFFORTS.map((e) => (
            <Menu.RadioItem key={e} value={e} className={menuItemClass}>
              <span className="w-4">
                <Menu.ItemIndicator>
                  <Check size={14} />
                </Menu.ItemIndicator>
              </span>
              {t(`effort.${e}`)}
            </Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
        <Menu.Separator className="my-1 h-px bg-line" />
        <div className="px-2 py-1.5 text-[11.5px] text-muted">
          {t('session.cost', { cost: formatUsd(session.costMicros) })}
        </div>
      </MenuContent>
    </Menu.Root>
  )
}

// ---------------------------------------------------------------- pending

function PendingPanel() {
  const { client, sessions, me } = useBoardCtx()
  const { t } = useI18n()
  const column = useColumn()
  const board = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const [picked, setPicked] = useState<Set<string>>(() => new Set())
  const [open, setOpen] = useState(true)
  const pending = useMemo(
    () =>
      [...board.state.items.values()]
        .filter(
          (i): i is ItemOf<'message'> =>
            i.kind === 'message' &&
            i.data.author.kind === 'user' &&
            i.data.author.id === me.id &&
            !snap.fired[i.id]?.length,
        )
        .sort((a, b) => a.createdAt - b.createdAt),
    [board.state.items, snap.fired, me.id],
  )
  if (!pending.length) return null
  // Picks that were handed over or deleted meanwhile no longer count.
  const live = pending.filter((p) => picked.has(p.id)).map((p) => p.id)
  const chosen = live.length ? live : pending.map((p) => p.id)
  return (
    <section
      aria-label={t('pending.title')}
      className="flex-none border-b border-line bg-surface-2/60"
    >
      <div className={column}>
        <div className="flex h-9 items-center gap-2 px-3">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => {
              setOpen((v) => !v)
            }}
            className="flex items-center gap-1.5 text-[12.5px] font-medium text-ink-2"
          >
            <ChevronDown
              size={13}
              className={clsx('transition-transform', !open && '-rotate-90')}
            />
            {t('pending.title')}
            <span className="rounded-full bg-surface px-1.5 text-[11px] text-muted">
              {pending.length}
            </span>
          </button>
          <span className="flex-1" />
          <HandToAiMenu
            messageIds={chosen}
            onDone={() => {
              setPicked(new Set())
            }}
          >
            <button
              type="button"
              className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12px] font-medium text-ai hover:bg-ai-soft"
            >
              <Send size={12} />
              {live.length ? t('pending.handPicked', { n: live.length }) : t('pending.handAll')}
            </button>
          </HandToAiMenu>
        </div>
        {open && (
          <ul className="max-h-40 space-y-0.5 overflow-y-auto px-2 pb-2">
            {pending.map((m) => (
              <li
                key={m.id}
                className="flex items-start gap-2 rounded-md px-1.5 py-1 hover:bg-surface"
              >
                <input
                  type="checkbox"
                  aria-label={m.data.text}
                  checked={picked.has(m.id)}
                  onChange={(e) => {
                    setPicked((cur) => {
                      const next = new Set(cur)
                      if (e.target.checked) next.add(m.id)
                      else next.delete(m.id)
                      return next
                    })
                  }}
                  className="mt-1 accent-(--ai)"
                />
                <span className="min-w-0 flex-1 text-[12.5px] leading-snug">
                  <MentionChip mention={{ kind: 'item', id: m.id }} fallback={m.data.text} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

/** Put this conversation on the board (only people do), or show it if it is already there. */
function PlaceOnBoardItem({ sessionId }: { sessionId: string }) {
  const { client, me, engine, actions } = useBoardCtx()
  const { t } = useI18n()
  const board = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const node = [...board.state.items.values()].find(
    (i) => i.kind === 'session' && i.data.sessionId === sessionId,
  )
  return (
    <Menu.Item
      className={menuItemClass}
      onSelect={() => {
        if (node) {
          actions.current?.showItems([node.id])
          return
        }
        const c = engine.current?.viewportCenter() ?? { x: 0, y: 0 }
        const item = newSessionNode(client.view, me, c, sessionId)
        client.apply([{ type: 'item.create', item }])
        setTimeout(() => {
          actions.current?.showItems([item.id])
        }, 50)
      }}
    >
      <LayoutDashboard size={13} className="text-muted" />
      {node ? t('session.showOnBoard') : t('session.placeOnBoard')}
    </Menu.Item>
  )
}

// ---------------------------------------------------------------- chat log

function TriggerView({ trigger, queued }: { trigger: Trigger; queued?: boolean }) {
  const { t } = useI18n()
  return (
    <>
      {trigger.fired && trigger.fired.length > 0 && (
        <div className="text-center text-[12px] text-muted">
          {t('chat.handed', { n: trigger.fired.length })}{' '}
          {trigger.fired.map((id) => (
            <span key={id} hidden data-board-message={id} />
          ))}
          {trigger.fired.slice(0, 3).map((id) => (
            <MentionChip key={id} mention={{ kind: 'item', id }} fallback="…" />
          ))}
        </div>
      )}
      {trigger.chat && (trigger.chat.text || trigger.chat.files?.length) ? (
        <div className="flex flex-col items-end gap-1">
          <div className="max-w-[88%] rounded-[14px_14px_4px_14px] border border-line bg-bg px-3 py-2 text-[13.5px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">
            <TextWithPointers text={trigger.chat.text} />
            {trigger.chat.files && trigger.chat.files.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {trigger.chat.files.map((f) => (
                  <span
                    key={f.fileKey}
                    className="inline-flex items-center gap-1 rounded border border-line px-1.5 text-[11.5px] text-muted"
                  >
                    <FileText size={11} />
                    {f.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {queued && <span className="text-[11px] text-muted">{t('chat.queued')}</span>}
        </div>
      ) : null}
    </>
  )
}

/** One thing the AI said; while it streams, revealed at a steady pace with new blocks fading in. */
function SaidView({
  text,
  live,
  since,
  boardMessageId,
}: {
  text: string
  live: boolean
  since: number | null
  boardMessageId?: string | undefined
}) {
  // Once streamed, keep the class: toggling it would replay the fade on every block.
  const [streamed] = useState(live)
  const shown = useSmoothText(text, live)
  return (
    <div
      className={clsx('text-[13.5px] leading-relaxed', streamed && 'say-streamed')}
      {...(live && { 'aria-live': 'polite' })}
      {...(boardMessageId && { 'data-board-message': boardMessageId })}
    >
      <SayText text={shown} since={since} />
    </div>
  )
}

function TurnView({
  turn,
  sessionId,
  onTrace,
}: {
  turn: Turn
  sessionId: string
  onTrace: (turnId: string, tab?: PanelTab) => void
}) {
  const { t } = useI18n()
  const { sessions } = useBoardCtx()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const reverts = (snap.reverts[sessionId] ?? []).filter((r) => r.turnId === turn.turnId)
  const speaker = useSpeaker(sessionId)
  // The turn changed the board: its change view (改動) is worth a link.
  const changed = viewActions(turn.actions).length > 0
  return (
    <TurnIdContext value={turn.turnId}>
      <div className="group/turn space-y-3">
        {turn.board && (
          <BoardLine
            text={turn.board}
            view={turn.boardView}
            sessionId={sessionId}
            onRaw={() => {
              onTrace(turn.turnId)
            }}
          />
        )}
        <TriggerView trigger={turn.trigger} />
        {/* While it works, what it is doing; once done, the answer comes first. */}
        {!turn.end && <ActionsLine actions={turn.actions} />}
        {turn.said.map((s) => (
          <SaidView
            key={s.id}
            text={s.text}
            live={s.live}
            since={turn.end?.ts ?? null}
            boardMessageId={s.boardMessageId}
          />
        ))}
        {turn.end?.error && (
          <div
            role="alert"
            className="space-y-1 rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-[12.5px] text-danger"
          >
            <p>{turn.end.code === 'context_full' ? t('chat.contextFull') : t('chat.failed')}</p>
            <p className="font-mono text-[11px] break-words opacity-80">{turn.end.error}</p>
          </div>
        )}
        {turn.end?.aborted && <div className="text-[12px] text-muted">{t('chat.stopped')}</div>}
        {turn.end && !turn.end.error && !turn.end.aborted && !turn.said.length && (
          <div className="text-[12px] text-muted">{t('chat.silent', { who: speaker })}</div>
        )}
        {turn.end && <ActionsLine actions={turn.actions} />}
        {turn.end && (
          // Bookkeeping, there when looked for: on hover or focus (always on touch).
          <div className="flex items-center gap-3 text-[11.5px] text-muted opacity-0 transition-opacity duration-150 group-hover/turn:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
            {changed && (
              <button
                type="button"
                onClick={() => {
                  onTrace(turn.turnId, 'changes')
                }}
                className="hover:text-ink"
              >
                {t('change.open')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                onTrace(turn.turnId)
              }}
              className="hover:text-ink"
            >
              {t('chat.trace')}
            </button>
          </div>
        )}
        {reverts.map((r) => (
          <RevertNote key={r.id} r={r} />
        ))}
      </div>
    </TurnIdContext>
  )
}

const STATUS_KEYS: Record<string, string> = {
  thinking: 'status.thinking',
  writing: 'status.writing',
  'creating a card': 'status.creatingCard',
  'editing a card': 'status.editingCard',
  'rewriting a card': 'status.editingCard',
  'adding a link': 'status.addingLink',
  arranging: 'status.arranging',
  'removing items': 'status.removing',
  connecting: 'status.connecting',
  'updating a connection': 'status.connecting',
  grouping: 'status.grouping',
  'updating a group': 'status.grouping',
  'reading a web page': 'status.reading',
}

function ChatLog({ onTrace }: { onTrace: (turnId: string, tab?: PanelTab) => void }) {
  const { sessions } = useBoardCtx()
  const { t } = useI18n()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const cur = snap.current
  const tr = cur ? snap.transcripts[cur] : undefined
  const turns = useMemo(
    () => buildTurns(tr?.entries ?? [], (cur && snap.partials[cur]) || {}),
    [tr?.entries, snap.partials, cur],
  )
  const meta = snap.sessions.find((s) => s.id === cur)
  const running = !!meta?.running
  const status = cur ? snap.status[cur] : null
  const doing = running ? actionStatus(turns.at(-1)?.actions ?? [], t) : null
  const scroller = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  const column = useColumn()
  const main = useChatVariant() === 'main'

  // Follow new content while the person is at the bottom.
  useEffect(() => {
    const el = scroller.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
  }, [turns, tr?.queued, status])

  // Asked from the board to show one of its messages here: scroll to it and mark it.
  const reveal = snap.reveal
  const revealed = useRef(0)
  useEffect(() => {
    if (!reveal?.messageId || reveal.sessionId !== cur || revealed.current === reveal.n) return
    const el = scroller.current?.querySelector<HTMLElement>(
      `[data-board-message="${CSS.escape(reveal.messageId)}"]`,
    )
    if (!el) return
    revealed.current = reveal.n
    const target = el.hidden ? el.parentElement : el
    if (!target) return
    stick.current = false
    target.scrollIntoView({ block: 'center', behavior: 'smooth' })
    target.classList.remove('moot-chat-flash')
    target.getBoundingClientRect()
    target.classList.add('moot-chat-flash')
    setTimeout(() => {
      target.classList.remove('moot-chat-flash')
    }, 1800)
  }, [reveal, cur, turns])

  const empty = !turns.length && !tr?.queued.length
  return (
    <div
      ref={scroller}
      onScroll={(e) => {
        const el = e.currentTarget
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
      }}
      className="min-h-0 flex-1 overflow-y-auto"
    >
      <div className={clsx('space-y-5', main ? 'px-4 py-6 sm:px-6 sm:py-8' : 'px-4 py-4', column)}>
        {empty && (
          <div className="mt-10 space-y-2 px-2 text-center">
            <p className="text-[14px] font-medium">{t('chat.emptyTitle')}</p>
            <p className="text-[12.5px] leading-relaxed text-balance text-muted">
              {/* In chat-main the board is out of sight: no "write on the board" hint. */}
              {t(main ? 'chat.emptyBodyMain' : 'chat.emptyBody')}
            </p>
          </div>
        )}
        {turns.map((turn) => (
          <TurnView key={turn.turnId} turn={turn} sessionId={cur ?? ''} onTrace={onTrace} />
        ))}
        {running && cur && (
          <div className="flex items-center gap-2 text-[12.5px] text-muted" role="status">
            <span className="size-1.5 animate-pulse rounded-full bg-ai" />
            <span className="flex-1">
              {doing ??
                t((STATUS_KEYS[status ?? 'thinking'] ?? 'status.thinking') as 'status.thinking')}
            </span>
            <button
              type="button"
              onClick={() => {
                sessions.stop(cur)
              }}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-line px-2 text-[12px] text-ink-2 hover:border-line-strong hover:text-ink"
            >
              <Square size={10} fill="currentColor" />
              {t('chat.stop')}
            </button>
          </div>
        )}
        {tr?.queued.map((q, i) => (
          <TriggerView key={`q${i}`} trigger={q} queued />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- next turn

/**
 * What the AI would be told about the board if you sent now, by the composer
 * (DESIGN.md principle 1: the context is visible). Asked of the server, so it
 * is computed exactly as the real turn will be; refreshed as the board, card
 * text or the conversation change.
 */
function NextTurnBoard() {
  const { client, sessions } = useBoardCtx()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const cur = snap.current
  const entries = cur ? snap.transcripts[cur]?.entries.length : undefined
  const pending = cur ? snap.pending[cur] : undefined
  const column = useColumn()

  useEffect(() => {
    if (!cur) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const ask = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        sessions.peek(cur)
      }, 500)
    }
    sessions.peek(cur)
    const off = client.subscribe(ask)
    client.doc.on('update', ask)
    return () => {
      clearTimeout(timer)
      off()
      client.doc.off('update', ask)
    }
  }, [cur, client, sessions, entries])

  if (!cur || !pending?.text) return null
  return (
    <div className="flex-none px-3 pt-2">
      <div className={column}>
        <BoardLine text={pending.text} view={pending.view} sessionId={cur} pending />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- panel

/**
 * The conversation: session header, board messages waiting for the AI, the
 * chat, the next turn's board preview and the composer. The same in both
 * layouts; chat-main centres it as a reading column.
 */
export function ChatPanel({
  variant,
  trailing,
  headerSlot = null,
}: {
  variant: ChatVariant
  trailing: ReactNode
  /** Where to put the session controls instead of a bar of their own (the app header). */
  headerSlot?: HTMLElement | null
}) {
  const { sessions, client } = useBoardCtx()
  const { t } = useI18n()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const cur = snap.current
  const [panel, setTrace] = useState<{
    /** The conversation it is about: switching conversations closes it. */
    session: string | null
    scope: string | null
    tab: PanelTab
    refs: string[]
  } | null>(null)
  const trace = panel?.session === cur ? panel : null
  const openPanel = useCallback<OpenTurnPanel>(
    (turnId, tab, refs = []) => {
      setTrace({ session: cur, scope: turnId, tab, refs })
    },
    [cur],
  )
  const entries = (cur && snap.transcripts[cur]?.entries) || []
  /** A conversation an outside app (MCP) drives: nothing typed here would reach it. */
  const external = snap.sessions.find((s) => s.id === cur)?.external

  // Pick the most recent session when none is chosen yet.
  const { loaded, current, sessions: list } = snap
  useEffect(() => {
    if (!loaded || current) return
    const latest = [...list].sort((a, b) => b.updatedAt - a.updatedAt)[0]
    if (latest) sessions.select(latest.id)
  }, [loaded, current, list, sessions])

  const Root = variant === 'main' ? 'section' : 'aside'
  return (
    <ChatVariantContext value={variant}>
      <Root
        aria-label={t('sidebar.label')}
        data-chat={variant}
        className={clsx(
          'relative flex h-full flex-col bg-surface',
          variant === 'main'
            ? 'min-w-0 flex-1'
            : // Narrow screens: no room beside the canvas, so it covers it.
              'w-[380px] flex-none border-l border-line max-[899px]:absolute max-[899px]:inset-0 max-[899px]:z-20 max-[899px]:w-full max-[899px]:border-l-0',
        )}
      >
        {headerSlot ? (
          createPortal(
            <SessionHeader
              inline
              trailing={trailing}
              onTrace={() => {
                setTrace({ session: cur, scope: null, tab: 'trace', refs: [] })
              }}
            />,
            headerSlot,
          )
        ) : (
          <SessionHeader
            trailing={trailing}
            onTrace={() => {
              setTrace({ session: cur, scope: null, tab: 'trace', refs: [] })
            }}
          />
        )}
        {snap.error && (snap.error.sessionId === null || snap.error.sessionId === cur) && (
          <ErrorBanner code={snap.error.code} />
        )}
        {!client.readOnly && <PendingPanel />}
        <OpenTurnPanelContext value={openPanel}>
          <ChatLog
            onTrace={(turnId, tab = 'trace') => {
              setTrace({ session: cur, scope: turnId, tab, refs: [] })
            }}
          />
        </OpenTurnPanelContext>
        {/* An outside app reads the board when it chooses; there is no next message to preview. */}
        {!client.readOnly && !external && <NextTurnBoard />}
        {!client.readOnly && !external && <ChatComposer />}
        {external && (
          <p className="flex-none border-t border-line px-4 py-3 text-center text-[12.5px] text-muted">
            {t('chat.external', { client: external.client })}{' '}
            <Link to="/settings#apps" className="underline underline-offset-2 hover:text-ink">
              {t('apps.manage')}
            </Link>
          </p>
        )}
        {trace && cur && (
          <TracePanel
            entries={entries}
            scope={trace.scope}
            onScope={(scope) => {
              setTrace({ ...trace, scope })
            }}
            onClose={() => {
              setTrace(null)
            }}
            tab={trace.tab}
            onTab={(tab) => {
              setTrace({ ...trace, tab })
            }}
            changes={
              trace.scope && (
                <ChangeView
                  key={`${trace.scope}:${trace.refs.join(',')}`}
                  sessionId={cur}
                  turnId={trace.scope}
                  focusRefs={trace.refs}
                />
              )
            }
          />
        )}
      </Root>
    </ChatVariantContext>
  )
}

function ErrorBanner({ code }: { code: string }) {
  const { t } = useI18n()
  const column = useColumn()
  return (
    <div
      role="alert"
      className="flex-none border-b border-line bg-me-soft px-3 py-2.5 text-[12.5px]"
    >
      <div className={column}>
        <p className="font-medium text-ink">
          {code === 'trial_exhausted'
            ? t('error.trialTitle')
            : code === 'too_old'
              ? t('error.tooOld')
              : code === 'busy'
                ? t('error.busy')
                : t('error.generic')}
        </p>
        {code === 'trial_exhausted' && (
          <p className="mt-0.5 text-ink-2">
            {t('error.trialBody')}{' '}
            <Link to="/settings" className="font-medium text-ai underline underline-offset-2">
              {t('error.trialAction')}
            </Link>
          </p>
        )}
      </div>
    </div>
  )
}

/** Canvas-main: the conversation as a sidebar next to the whiteboard. */
export function Sidebar({
  onCollapse,
  onChatMain,
}: {
  onCollapse: () => void
  /** Switch this board to chat-main. */
  onChatMain: () => void
}) {
  const { t } = useI18n()
  return (
    <ChatPanel
      variant="sidebar"
      trailing={
        <>
          <IconButton label={t('layout.toChat')} onClick={onChatMain}>
            <MessagesSquare size={16} />
          </IconButton>
          <IconButton label={t('sidebar.collapse')} onClick={onCollapse}>
            <PanelRightClose size={16} />
          </IconButton>
        </>
      }
    />
  )
}
